import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Box,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem as SelectMenuItem,
  Alert,
  Button,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  AutoAwesome as AutoAwesomeIcon,
  SupportAgent as SupportAgentIcon,
  RocketLaunch as RocketLaunchIcon,
  Psychology as PsychologyIcon,
  Campaign as CampaignIcon,
  HeadsetMic as HeadsetMicIcon,
  ShoppingCart as ShoppingCartIcon,
  Business as BusinessIcon,
  School as SchoolIcon,
  HealthAndSafety as HealthAndSafetyIcon,
  Engineering as EngineeringIcon,
  AccountBalance as AccountBalanceIcon,
  Restaurant as RestaurantIcon,
  Home as HomeIcon,
  DirectionsCar as DirectionsCarIcon,
  Computer as ComputerIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Chat as ChatIcon,
  Forum as ForumIcon,
  QuestionAnswer as QuestionAnswerIcon,
  Help as HelpIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Star as StarIcon,
  Favorite as FavoriteIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Settings as SettingsIcon,
  Build as BuildIcon,
  Tune as TuneIcon,
  Palette as PaletteIcon,
  Brush as BrushIcon,
  Create as CreateIcon,
  Edit as EditIcon,
  ContentCopy as ContentCopyIcon,
  Print as PrintIcon,
  Save as SaveIcon,
  CloudUpload as CloudUploadIcon,
  CloudDownload as CloudDownloadIcon,
  Sync as SyncIcon,
  RestartAlt as RestartAltIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  SkipNext as SkipNextIcon,
  SkipPrevious as SkipPreviousIcon,
  FastForward as FastForwardIcon,
  FastRewind as FastRewindIcon,
  VolumeUp as VolumeUpIcon,
  VolumeDown as VolumeDownIcon,
  VolumeOff as VolumeOffIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Videocam as VideocamIcon,
  VideocamOff as VideocamOffIcon,
  CameraAlt as CameraAltIcon,
  PhotoCamera as PhotoCameraIcon,
  Image as ImageIcon,
  VideoLibrary as VideoLibraryIcon,
  MusicNote as MusicNoteIcon,
  LibraryMusic as LibraryMusicIcon,
  Movie as MovieIcon,
  Theaters as TheatersIcon,
  Sports as SportsIcon,
  FitnessCenter as FitnessCenterIcon,
  Pool as PoolIcon,
  DirectionsRun as DirectionsRunIcon,
  DirectionsBike as DirectionsBikeIcon,
  DirectionsWalk as DirectionsWalkIcon,
  Flight as FlightIcon,
  Train as TrainIcon,
  DirectionsBus as DirectionsBusIcon,
  LocalTaxi as LocalTaxiIcon,
  Hotel as HotelIcon,
  LocationOn as LocationOnIcon,
  MyLocation as MyLocationIcon,
  Navigation as NavigationIcon,
  Map as MapIcon,
  Public as PublicIcon,
  Language as LanguageIcon,
  Translate as TranslateIcon,
  GTranslate as GTranslateIcon,
  Menu as MenuIcon,
  MoreVert as MoreVertIcon,
  MoreHoriz as MoreHorizIcon,
  Apps as AppsIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
  ViewComfy as ViewComfyIcon,
  ViewStream as ViewStreamIcon,
  ViewColumn as ViewColumnIcon,
  ViewWeek as ViewWeekIcon,
  ViewDay as ViewDayIcon,
  ViewAgenda as ViewAgendaIcon,
  ViewCarousel as ViewCarouselIcon,
  ViewQuilt as ViewQuiltIcon,
  ViewSidebar as ViewSidebarIcon,
  ViewInAr as ViewInArIcon,
  ViewArray as ViewArrayIcon,
  ViewCompact as ViewCompactIcon,
  ViewHeadline as ViewHeadlineIcon,
  ViewKanban as ViewKanbanIcon,
  ViewTimeline as ViewTimelineIcon,
} from '@mui/icons-material';
import { Agent } from '../../types/agent';
import { Template } from '../../utils/templateApi';
import templateApi from '../../utils/templateApi';
import AgentHelpModal from './AgentHelpModal';
import toast from 'react-hot-toast';

type TemplateSelectionDialogProps = {
  open: boolean;
  onClose: () => void;
  onSelectTemplate: (template: Partial<Agent>) => void;
};

// Icon mapping for display
const iconMap: Record<string, React.ComponentType<any>> = {
  AutoAwesome: AutoAwesomeIcon,
  SupportAgent: SupportAgentIcon,
  RocketLaunch: RocketLaunchIcon,
  Psychology: PsychologyIcon,
  Campaign: CampaignIcon,
  HeadsetMic: HeadsetMicIcon,
  ShoppingCart: ShoppingCartIcon,
  Business: BusinessIcon,
  School: SchoolIcon,
  HealthAndSafety: HealthAndSafetyIcon,
  Engineering: EngineeringIcon,
  AccountBalance: AccountBalanceIcon,
  Restaurant: RestaurantIcon,
  Home: HomeIcon,
  DirectionsCar: DirectionsCarIcon,
  Computer: ComputerIcon,
  Phone: PhoneIcon,
  Email: EmailIcon,
  Chat: ChatIcon,
  Forum: ForumIcon,
  QuestionAnswer: QuestionAnswerIcon,
  Help: HelpIcon,
  Info: InfoIcon,
  Warning: WarningIcon,
  Error: ErrorIcon,
  CheckCircle: CheckCircleIcon,
  Cancel: CancelIcon,
  Star: StarIcon,
  Favorite: FavoriteIcon,
  ThumbUp: ThumbUpIcon,
  ThumbDown: ThumbDownIcon,
  Share: ShareIcon,
  Download: DownloadIcon,
  Upload: UploadIcon,
  Settings: SettingsIcon,
  Build: BuildIcon,
  Tune: TuneIcon,
  Palette: PaletteIcon,
  Brush: BrushIcon,
  Create: CreateIcon,
  Edit: EditIcon,
  ContentCopy: ContentCopyIcon,
  Print: PrintIcon,
  Save: SaveIcon,
  CloudUpload: CloudUploadIcon,
  CloudDownload: CloudDownloadIcon,
  Sync: SyncIcon,
  RestartAlt: RestartAltIcon,
  PlayArrow: PlayArrowIcon,
  Pause: PauseIcon,
  Stop: StopIcon,
  SkipNext: SkipNextIcon,
  SkipPrevious: SkipPreviousIcon,
  FastForward: FastForwardIcon,
  FastRewind: FastRewindIcon,
  VolumeUp: VolumeUpIcon,
  VolumeDown: VolumeDownIcon,
  VolumeOff: VolumeOffIcon,
  Mic: MicIcon,
  MicOff: MicOffIcon,
  Videocam: VideocamIcon,
  VideocamOff: VideocamOffIcon,
  CameraAlt: CameraAltIcon,
  PhotoCamera: PhotoCameraIcon,
  Image: ImageIcon,
  VideoLibrary: VideoLibraryIcon,
  MusicNote: MusicNoteIcon,
  LibraryMusic: LibraryMusicIcon,
  Movie: MovieIcon,
  Theaters: TheatersIcon,
  Sports: SportsIcon,
  FitnessCenter: FitnessCenterIcon,
  Pool: PoolIcon,
  DirectionsRun: DirectionsRunIcon,
  DirectionsBike: DirectionsBikeIcon,
  DirectionsWalk: DirectionsWalkIcon,
  Flight: FlightIcon,
  Train: TrainIcon,
  DirectionsBus: DirectionsBusIcon,
  LocalTaxi: LocalTaxiIcon,
  Hotel: HotelIcon,
  LocationOn: LocationOnIcon,
  MyLocation: MyLocationIcon,
  Navigation: NavigationIcon,
  Map: MapIcon,
  Public: PublicIcon,
  Language: LanguageIcon,
  Translate: TranslateIcon,
  GTranslate: GTranslateIcon,
  Menu: MenuIcon,
  MoreVert: MoreVertIcon,
  MoreHoriz: MoreHorizIcon,
  Apps: AppsIcon,
  GridView: GridViewIcon,
  ViewList: ViewListIcon,
  ViewModule: ViewModuleIcon,
  ViewComfy: ViewComfyIcon,
  ViewStream: ViewStreamIcon,
  ViewColumn: ViewColumnIcon,
  ViewWeek: ViewWeekIcon,
  ViewDay: ViewDayIcon,
  ViewAgenda: ViewAgendaIcon,
  ViewCarousel: ViewCarouselIcon,
  ViewQuilt: ViewQuiltIcon,
  ViewSidebar: ViewSidebarIcon,
  ViewInAr: ViewInArIcon,
  ViewArray: ViewArrayIcon,
  ViewCompact: ViewCompactIcon,
  ViewHeadline: ViewHeadlineIcon,
  ViewKanban: ViewKanbanIcon,
  ViewTimeline: ViewTimelineIcon,
};

const getIconForTemplate = (iconName: string) => {
  if (iconName && iconMap[iconName]) {
    return React.createElement(iconMap[iconName], { fontSize: "large" });
  }
  // Fallback to a default icon if no icon is specified or icon not found
  return <SearchIcon fontSize="large" />;
};

const TemplateSelectionDialog: React.FC<TemplateSelectionDialogProps> = ({ open, onClose, onSelectTemplate }) => {
  const theme = useTheme();
  const [helpOpen, setHelpOpen] = useState(false);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const [templatesPerPage] = useState(6);

  const categories = Array.from(new Set(templates.map(t => t.category).filter(Boolean)));
  const types = ['Lead', 'Assistente', 'Ambos'];

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const templatesData = await templateApi.fetchTemplates();
      setTemplates(templatesData);
    } catch (error) {
      console.error('Error loading templates:', error);
      toast.error('Erro ao carregar templates');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadTemplates();
    }
  }, [open]);

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = !searchText ||
      template.name.toLowerCase().includes(searchText.toLowerCase()) ||
      template.description.toLowerCase().includes(searchText.toLowerCase()) ||
      template.category.toLowerCase().includes(searchText.toLowerCase());

    const matchesCategory = !categoryFilter || template.category === categoryFilter;
    const matchesType = !typeFilter || template.type === typeFilter;

    return matchesSearch && matchesCategory && matchesType && template.status === 'Enabled';
  });

  const paginatedTemplates = filteredTemplates.slice(
    (page - 1) * templatesPerPage,
    page * templatesPerPage
  );

  const totalPages = Math.ceil(filteredTemplates.length / templatesPerPage);

  const convertTemplateToAgent = (template: Template): Partial<Agent> => {
    return {
      name: template.name,
      description: template.description,
      model: template.model as any,
      typeProduct: template.type as any,
      status: template.status as any,
      prompts: template.prompts.map(p => ({
        role: 'assistant' as 'system' | 'user' | 'assistant', // Sempre força como assistant
        content: p.content
      })),
      responseDelay: template.responseDelay
    };
  };

  const handleSelectTemplate = (template: Template) => {
    const agentData = convertTemplateToAgent(template);
    onSelectTemplate(agentData);
  };

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    setPage(1);
  };

  const handleCategoryChange = (value: string) => {
    setCategoryFilter(value);
    setPage(1);
  };

  const handleTypeChange = (value: string) => {
    setTypeFilter(value);
    setPage(1);
  };

  const handleRefresh = () => {
    loadTemplates();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        Escolha um template
        <Box>
          <IconButton aria-label="refresh" onClick={handleRefresh} size="small" sx={{ mr: 0.5 }}>
            <RefreshIcon />
          </IconButton>
          <IconButton aria-label="help" onClick={() => setHelpOpen(true)} size="small" sx={{ mr: 0.5 }}>
            <HelpOutlineIcon />
          </IconButton>
          <IconButton aria-label="close" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Search and Filters */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Buscar templates..."
            value={searchText}
            onChange={(e) => handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250, flexGrow: 1 }}
          />

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Categoria</InputLabel>
            <Select
              value={categoryFilter}
              onChange={(e) => handleCategoryChange(e.target.value)}
              label="Categoria"
            >
              <SelectMenuItem value="">Todas</SelectMenuItem>
              {categories.map((category) => (
                <SelectMenuItem key={category} value={category}>
                  {category}
                </SelectMenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Tipo</InputLabel>
            <Select
              value={typeFilter}
              onChange={(e) => handleTypeChange(e.target.value)}
              label="Tipo"
            >
              <SelectMenuItem value="">Todos</SelectMenuItem>
              {types.map((type) => (
                <SelectMenuItem key={type} value={type}>
                  {type}
                </SelectMenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Results Count */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {filteredTemplates.length} {filteredTemplates.length === 1 ? 'template encontrado' : 'templates encontrados'}
          </Typography>
        </Box>

        {/* Loading State */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : filteredTemplates.length === 0 ? (
          <Alert severity="info" sx={{ textAlign: 'center' }}>
            {searchText || categoryFilter || typeFilter
              ? 'Nenhum template encontrado com os filtros aplicados'
              : 'Nenhum template disponível no momento'
            }
          </Alert>
        ) : (
          <>
            {/* Templates Grid */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3 }}>
              {paginatedTemplates.map((template) => (
                <Box key={template.id}>
                  <Card elevation={3} sx={{ borderRadius: 3, height: '100%' }}>
                    <CardActionArea onClick={() => handleSelectTemplate(template)} sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1 }}>
                          <Box sx={{
                            width: 56,
                            height: 56,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: 'secondary.light',
                            color: theme.palette.mode === 'dark' ? '#000' : undefined,
                            mb: 1
                          }}>
                            {getIconForTemplate(template.icon)}
                          </Box>
                          <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {template.name}
                          </Typography>
                          <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
                            <Chip label={template.type} sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }} size="small" />
                            <Chip label={template.model} size="small" variant="outlined" />
                            {template.category && (
                              <Chip label={template.category} size="small"  variant="outlined" />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                            Delay: {template.responseDelay}ms
                          </Typography>
                        </Box>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Box>
              ))}
            </Box>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(_, newPage) => setPage(newPage)}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <AgentHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} topic="template-selection" />
    </Dialog>
  );
};

export default TemplateSelectionDialog;


